/**
 * DocumentEmissionForm
 * Formulário da emissão: empresa → tipo → alcance → período → bloco de assinaturas.
 *
 * A empresa vem PRIMEIRO de propósito: `GET /documents/types` filtra os tipos pelo
 * módulo que a empresa contratou. Escolher o tipo antes obrigaria a limpar o campo
 * assim que a empresa mudasse.
 *
 * @version 2026.1.0
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import {
  Description as DocxIcon,
  PictureAsPdf as PdfIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { CompanySelectField } from '../../../components/forms/CompanySelectField';
import { PrerequisiteBlock, type PrerequisiteBlockProps } from '../../../components/common';
import { SignatureBlockSelector } from './SignatureBlockSelector';
import { DocumentPreview } from './DocumentPreview';
import { ActionButton } from '../../../components/common';
import { PendencyList } from './PendencyList';
import documentService from '../../../services/documentService';
import departmentService from '../../../services/departmentService';
import occupationService from '../../../services/occupationService';
import employeeService from '../../../services/employeeService';
import { prerequisiteFromError } from '../../../utils/prerequisiteFromError';
import { SCOPE_LABELS, type RiskApplicationScope } from '../../../types/riskApplication';
import type {
  DocumentFormat,
  DocumentTypeSummary,
  EmissionRequest,
  PreviewResult,
  ReportType,
  SignatureBlockItem,
} from '../../../types/document';

/** Opção de alvo do alcance (setor, cargo ou funcionário). */
interface ScopeTarget {
  id: number;
  label: string;
}

const mensagemDeErro = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

/**
 * No alcance `company` o alvo é a própria empresa — não há segunda lista a carregar.
 */
const carregarAlvos = async (
  scope: RiskApplicationScope,
  companyId: number
): Promise<ScopeTarget[]> => {
  if (scope === 'company') return [];

  if (scope === 'department') {
    const setores = await departmentService.getDepartmentsByCompanyId(String(companyId));
    return setores.map((setor) => ({ id: Number(setor.id), label: setor.name }));
  }

  if (scope === 'occupation') {
    const cargos = await occupationService.getOccupations({
      limit: 200,
      filters: { companyId },
    });
    return cargos.data.map((cargo) => ({ id: cargo.id, label: cargo.name }));
  }

  const funcionarios = await employeeService.getEmployeesByCompany(companyId);
  return funcionarios.map((funcionario) => ({ id: funcionario.id, label: funcionario.name }));
};

export const DocumentEmissionForm: React.FC = () => {
  const [companyId, setCompanyId] = useState<string>('');
  const [types, setTypes] = useState<DocumentTypeSummary[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [type, setType] = useState<ReportType | ''>('');
  const [scope, setScope] = useState<RiskApplicationScope | ''>('');
  const [targets, setTargets] = useState<ScopeTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [scopeId, setScopeId] = useState<number | ''>('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [signatureBlock, setSignatureBlock] = useState<SignatureBlockItem[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState<DocumentFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prerequisite, setPrerequisite] = useState<PrerequisiteBlockProps | null>(null);

  const companyIdNumber = companyId === '' ? null : Number(companyId);
  const selectedType = useMemo(
    () => types.find((item) => item.type === type) ?? null,
    [types, type]
  );
  // useMemo para a identidade do array não mudar a cada render — sem isso o efeito
  // que pré-seleciona o alcance único dispararia em todo render.
  const allowedScopes = useMemo<RiskApplicationScope[]>(
    () => selectedType?.allowedScopes ?? [],
    [selectedType]
  );

  // Empresa → tipos emissíveis. Tudo o que depende do tipo é zerado junto.
  useEffect(() => {
    if (companyIdNumber === null) {
      setTypes([]);
      setType('');
      return;
    }

    let ativo = true;
    setLoadingTypes(true);
    setError(null);

    documentService
      .listTypes(companyIdNumber)
      .then((lista) => {
        if (!ativo) return;
        setTypes(lista);
        setType('');
        setScope('');
        setScopeId('');
        setSignatureBlock([]);
      })
      .catch((err: unknown) => {
        if (!ativo) return;
        setError(mensagemDeErro(err, 'Não foi possível carregar os tipos de documento'));
      })
      .finally(() => {
        if (ativo) setLoadingTypes(false);
      });

    return () => {
      ativo = false;
    };
  }, [companyIdNumber]);

  // Tipo com um único alcance permitido já vem escolhido.
  useEffect(() => {
    if (allowedScopes.length === 1) {
      setScope(allowedScopes[0]);
    }
  }, [allowedScopes]);

  // Alcance → lista de alvos.
  useEffect(() => {
    if (companyIdNumber === null || scope === '') {
      setTargets([]);
      return;
    }

    if (scope === 'company') {
      setTargets([]);
      setScopeId(companyIdNumber);
      return;
    }

    let ativo = true;
    setLoadingTargets(true);
    setScopeId('');

    carregarAlvos(scope, companyIdNumber)
      .then((lista) => {
        if (ativo) setTargets(lista);
      })
      .catch((err: unknown) => {
        if (!ativo) return;
        setError(mensagemDeErro(err, 'Não foi possível carregar as opções do alcance'));
      })
      .finally(() => {
        if (ativo) setLoadingTargets(false);
      });

    return () => {
      ativo = false;
    };
  }, [companyIdNumber, scope]);

  const handleTypeChange = (event: SelectChangeEvent<string | number>): void => {
    setType(String(event.target.value) as ReportType);
    setScope('');
    setScopeId('');
    // O bloco é recomposto pelo seletor com os papéis sugeridos do novo tipo.
    setSignatureBlock([]);
    setPreview(null);
  };

  const handleScopeChange = (event: SelectChangeEvent<string | number>): void => {
    setScope(String(event.target.value) as RiskApplicationScope);
    setScopeId('');
    setPreview(null);
  };

  const handleScopeIdChange = (event: SelectChangeEvent<string | number>): void => {
    setScopeId(Number(event.target.value));
    setPreview(null);
  };

  /**
   * Estável (o seletor a usa como dependência de efeito). Zerar a prévia junto é
   * o que impede a geração de ser liberada por uma lista de pendências vencida.
   */
  const handleSignatureBlockChange = useCallback((itens: SignatureBlockItem[]): void => {
    setSignatureBlock(itens);
    setPreview(null);
  }, []);

  const handlePeriodStartChange = (valor: string): void => {
    setPeriodStart(valor);
    setPreview(null);
  };

  const handlePeriodEndChange = (valor: string): void => {
    setPeriodEnd(valor);
    setPreview(null);
  };

  /** Devolve `null` (e publica a mensagem) quando falta algum campo obrigatório. */
  const buildRequest = useCallback((): EmissionRequest | null => {
    if (type === '' || scope === '' || scopeId === '') {
      setError('Escolha empresa, tipo de documento e alcance antes de gerar.');
      return null;
    }

    if (!periodStart || !periodEnd) {
      setError('Informe o período de vigência do documento.');
      return null;
    }

    return { type, scope, scopeId, periodStart, periodEnd, signatureBlock };
  }, [type, scope, scopeId, periodStart, periodEnd, signatureBlock]);

  const handlePreview = async (): Promise<void> => {
    const request = buildRequest();
    if (!request) return;

    setPreviewing(true);
    setError(null);
    // Limpa o bloco de uma corrida anterior: a prévia nova já traz a lista de pendências
    // atualizada, então mantê-lo duplicaria a mesma informação na tela.
    setPrerequisite(null);

    try {
      const resultado = await documentService.preview(request);
      setPreview(resultado);
    } catch (err: unknown) {
      setPreview(null);
      setError(mensagemDeErro(err, 'Não foi possível gerar a prévia'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async (format: DocumentFormat): Promise<void> => {
    const request = buildRequest();
    if (!request) return;

    setGenerating(format);
    setError(null);
    setPrerequisite(null);

    try {
      const arquivo = await documentService.generate(request, format);
      documentService.triggerDownload(arquivo);
    } catch (err: unknown) {
      // A prévia sem bloqueio libera o botão, mas uma pendência pode surgir entre a
      // prévia e o clique — é a corrida que o servidor pega e este bloco cobre.
      const bloqueio = prerequisiteFromError(err);
      if (bloqueio) {
        setPrerequisite(bloqueio);
      } else {
        setError(mensagemDeErro(err, 'Não foi possível gerar o documento'));
      }
    } finally {
      setGenerating(null);
    }
  };

  const ocupado = generating !== null || previewing;

  /**
   * A geração só é liberada depois de uma prévia sem pendência bloqueante: é a
   * prévia que traz a lista, e o backend recusaria a geração de qualquer forma.
   */
  const temBloqueio =
    preview?.pendencies.some((pendencia) => pendencia.severity === 'bloqueia') ?? false;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Dados da emissão
        </Typography>

        {prerequisite && (
          <Box sx={{ mb: 2 }}>
            <PrerequisiteBlock {...prerequisite} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <CompanySelectField
            value={companyId}
            onChange={setCompanyId}
            grid={{ xs: 12, md: 6 }}
          />

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth disabled={companyIdNumber === null || loadingTypes}>
              <InputLabel id="document-type-label">Tipo de documento *</InputLabel>
              <Select
                labelId="document-type-label"
                label="Tipo de documento *"
                value={type}
                onChange={handleTypeChange}
              >
                {types.map((item) => (
                  <MenuItem key={item.type} value={item.type}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {companyIdNumber !== null && !loadingTypes && types.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Nenhum documento disponível para os módulos contratados por esta empresa.
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth disabled={allowedScopes.length === 0}>
              <InputLabel id="document-scope-label">Alcance *</InputLabel>
              <Select
                labelId="document-scope-label"
                label="Alcance *"
                value={scope}
                onChange={handleScopeChange}
              >
                {allowedScopes.map((opcao) => (
                  <MenuItem key={opcao} value={opcao}>
                    {SCOPE_LABELS[opcao]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {scope !== '' && scope !== 'company' && (
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={loadingTargets}>
                <InputLabel id="document-scope-id-label">{SCOPE_LABELS[scope]} *</InputLabel>
                <Select
                  labelId="document-scope-id-label"
                  label={`${SCOPE_LABELS[scope]} *`}
                  value={scopeId}
                  onChange={handleScopeIdChange}
                >
                  {targets.map((alvo) => (
                    <MenuItem key={alvo.id} value={alvo.id}>
                      {alvo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Início do período *"
              value={periodStart}
              onChange={(event) => handlePeriodStartChange(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Fim do período *"
              value={periodEnd}
              onChange={(event) => handlePeriodEndChange(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <SignatureBlockSelector
            companyId={companyIdNumber}
            type={type}
            value={signatureBlock}
            onChange={handleSignatureBlockChange}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 3 }}>
          <Button
            variant="contained"
            startIcon={
              previewing ? <CircularProgress size={18} color="inherit" /> : <PreviewIcon />
            }
            disabled={ocupado}
            onClick={() => void handlePreview()}
          >
            Gerar prévia
          </Button>
          <ActionButton
            acao="document_emission.generate"
            rotulo="Gerar PDF"
            variant="outlined"
            startIcon={
              generating === 'pdf' ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />
            }
            disabled={ocupado || temBloqueio}
            onClick={() => void handleGenerate('pdf')}
          >
            Gerar PDF
          </ActionButton>
          <ActionButton
            acao="document_emission.generate"
            rotulo="Gerar DOCX"
            variant="outlined"
            startIcon={
              generating === 'docx' ? <CircularProgress size={18} color="inherit" /> : <DocxIcon />
            }
            disabled={ocupado || temBloqueio}
            onClick={() => void handleGenerate('docx')}
          >
            Gerar DOCX
          </ActionButton>
        </Box>

        {temBloqueio && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            Há pendências que impedem a emissão. Resolva-as e gere a prévia novamente.
          </Typography>
        )}

        {preview && (
          <Box sx={{ mt: 3 }}>
            <PendencyList pendencies={preview.pendencies} />
            <Box sx={{ mt: 2 }}>
              <DocumentPreview html={preview.html} />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentEmissionForm;
